"use client";

// Inspired by react-hot-toast library
import * as React from "react";
import { isAuthErrorLike, signOutAndGoToSignIn } from "@/lib/clientAuthError";

import type { ToastActionElement, ToastProps } from "@/components/ui/toast";

const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 1000000;

type ToasterToast = ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
  image?: string;
  icon?: string;
};

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const;

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

type ActionType = typeof actionTypes;

type Action =
  | {
      type: ActionType["ADD_TOAST"];
      toast: ToasterToast;
    }
  | {
      type: ActionType["UPDATE_TOAST"];
      toast: Partial<ToasterToast>;
    }
  | {
      type: ActionType["DISMISS_TOAST"];
      toastId?: ToasterToast["id"];
    }
  | {
      type: ActionType["REMOVE_TOAST"];
      toastId?: ToasterToast["id"];
    };

interface State {
  toasts: ToasterToast[];
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    });
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
};

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t,
        ),
      };

    case "DISMISS_TOAST": {
      const { toastId } = action;

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id);
        });
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t,
        ),
      };
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
};

const listeners: Array<(state: State) => void> = [];

let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

type Toast = Omit<ToasterToast, "id">;

const nodeToText = (value: React.ReactNode): string => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map(nodeToText).join(" ");
  }
  if (React.isValidElement(value)) {
    const props = (value as React.ReactElement<{ children?: React.ReactNode }>)
      .props;
    return nodeToText(props?.children);
  }
  return "";
};

const shouldRedirectToSignIn = (props: Toast): boolean => {
  const haystack = `${nodeToText(props.title)} ${nodeToText(props.description)}`
    .toLowerCase()
    .trim();

  if (!haystack) return false;
  return isAuthErrorLike(haystack);
};

type ToastReturn = {
  id: string;
  dismiss: () => void;
  update: (props: ToasterToast) => void;
};

type ToastVariant = NonNullable<Toast["variant"]>;

interface ToastFn {
  (props: Toast): ToastReturn;
  success: (title: React.ReactNode, description?: React.ReactNode) => ToastReturn;
  error: (title: React.ReactNode, description?: React.ReactNode) => ToastReturn;
  warning: (title: React.ReactNode, description?: React.ReactNode) => ToastReturn;
  info: (title: React.ReactNode, description?: React.ReactNode) => ToastReturn;
}

// Next.js replaces the message of anything thrown out of a server action or
// server component with this text in production. It tells the user nothing, so
// no toast shows it. Actions should return their real reason (lib/actionResult);
// this is the net for whatever still throws.
const HIDDEN_SERVER_ERROR = /An error occurred in the Server Components render/i;

const withoutHiddenServerError = (props: Toast): Toast => {
  const isHidden = (value: React.ReactNode) =>
    typeof value === "string" && HIDDEN_SERVER_ERROR.test(value);
  if (!isHidden(props.title) && !isHidden(props.description)) return props;
  console.warn("A server error reached a toast with its message hidden by Next.js.");
  return {
    ...props,
    title: isHidden(props.title) ? "Something went wrong" : props.title,
    description: isHidden(props.description)
      ? "Something went wrong on our side. Please try again. If it keeps happening, please report it."
      : props.description,
  };
};

const toast = ((props: Toast): ToastReturn => {
  props = withoutHiddenServerError(props);
  if (typeof window !== "undefined" && shouldRedirectToSignIn(props)) {
    void signOutAndGoToSignIn();
    return {
      id: "auth-redirect",
      dismiss: () => {},
      update: (_props: ToasterToast) => {},
    };
  }

  const id = genId();

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    });
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id });

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      },
    },
  });

  return {
    id: id,
    dismiss,
    update,
  };
}) as ToastFn;

const makeVariantHelper =
  (variant: ToastVariant) =>
  (title: React.ReactNode, description?: React.ReactNode): ToastReturn =>
    toast({ variant, title, description } as Toast);

toast.success = makeVariantHelper("success");
toast.error = makeVariantHelper("error");
toast.warning = makeVariantHelper("warning");
toast.info = makeVariantHelper("info");

function useToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    // Sync immediately in case a toast was dispatched before this subscriber mounted.
    setState(memoryState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, []);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  };
}

export { useToast, toast };
